import { FocusEventHandler } from "react";
import { Omit } from "utility-types";
import { useFlyout, UseFlyoutOptions, UseFlyoutResult } from "../flyout";
import { useDelayedOpen } from "../flyout/hooks/useDelayedOpen";
import { useFocusMode } from "../focusMode";
import { tooltipFlyoutDefaults } from "./tooltipConstants";

export type UseTooltipOptions<TargetElement> = Omit<UseFlyoutOptions<TargetElement>, "isOpen" | "onCloseRequested"> & {
    /**
     * If specified, will adjust how quickly in ms the tooltip opens after mouse enters on button.
     * Default is 500ms
     */
    enterDelay?: number;

    /**
     * If specified, will be composed into the internally-used `onBlur` prop and spread
     * into the `targetProps` key on `UseTooltipResult`;
     */
    onBlur?: React.FocusEventHandler<TargetElement>;

    /**
     * If specified, will be composed into the internally-used `onFocus` prop and spread
     * into the `targetProps` key on `UseTooltipResult`;
     */
    onFocus?: React.FocusEventHandler<TargetElement>;

    /**
     * If specified, will be composed into the internally-used `onMouseEnter` prop and spread
     * into the `targetProps` key on `UseTooltipResult`;
     */
    onMouseEnter?: React.MouseEventHandler<TargetElement>;

    /**
     * If specified, will be composed into the internally-used `onMouseLeave` prop and spread
     * into the `targetProps` key on `UseTooltipResult`;
     */
    onMouseLeave?: React.MouseEventHandler<TargetElement>;

    /**
     * If true, the target's Tooltip will NOT be linked via aria-describedby to
     * the Tooltip. If the Tooltip contains text that might be redundant to
     * a screen reader user, you can use this to improve their experience.
     */

    visualOnly?: boolean;
};

export type UseTooltipResult<TargetElement> = Omit<UseFlyoutResult<TargetElement>, "flyoutProps" | "targetProps"> & {
    targetProps: Omit<UseFlyoutResult<TargetElement>["targetProps"], "aria-owns"> & {
        /**
         * Handler used to manage hover state on the target.
         */
        onMouseEnter: React.MouseEventHandler<TargetElement>;

        /**
         * Handler used to manage hover state on the target.
         */
        onMouseLeave: React.MouseEventHandler<TargetElement>;

        /**
         * This will be an id to match the target and tooltip.
         * If you provide an id, the Tooltip will use that.
         *
         * Only provided when the Tooltip is open, otherwise undefined.
         */
        "aria-describedby"?: string;

        /**
         * Handler used to manage focus state on the target
         */
        onBlur?: React.FocusEventHandler<TargetElement>;

        /**
         * Handler used to manage focus state on the target
         */
        onFocus?: React.FocusEventHandler<TargetElement>;
    };
    // Just aliasing this property for readability.
    tooltipProps: UseFlyoutResult<TargetElement>["flyoutProps"];
};

/**
 * Provides a convenience wrapper over the `useFlyout` hook with reasonable
 * defaults for a Tooltip.
 *
 * If `isOpen` is provided, we'll use it. Otherwise, set up some functionality to manage it internally.
 * This is an example of controlled/uncontrolled behavior that usually we try and stay away
 * from in lodestar-core, but Tooltip is an example of where the most common use-case will be
 * uncontrolled. We still want to provide our users with an escape hatch.
 */
export const useTooltip = <TargetElementType extends HTMLElement = HTMLButtonElement>({
    onMouseEnter,
    onMouseLeave,
    onFocus: providedOnFocus,
    onBlur,
    enterDelay = 500,
    visualOnly = false,
    ...rest
}: UseTooltipOptions<TargetElementType> = {}): UseTooltipResult<TargetElementType> => {
    const { isOpen, setIsOpen, cancelEnterTimer, getDelayedOpenTargetProps } = useDelayedOpen<TargetElementType>({
        enterDelay,
    });

    // When the user is in mouse mode, we have a special case here. When focus moves to the
    // tooltip's target we don't want to show the Tooltip because there's no indication that you're
    // focused on anything (no outline). It's awkward to see the tooltip pop up out of nowhere.
    const { getFocusMode } = useFocusMode({ passive: true });
    const onFocus: FocusEventHandler<TargetElementType> = (e) => {
        if (getFocusMode() === "mouse") {
            // Close the Tooltip if it's open already.
            setIsOpen(false);
            // After any timers are set by useDelayedOpen, cancel them so the Tooltip doesn't reopen
            // after the ususal timer started in useDelayedOpen.onFocus().
            requestAnimationFrame(() => {
                cancelEnterTimer();
            });
        }
        providedOnFocus?.(e);
    };

    const {
        flyoutProps,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        targetProps: { "aria-owns": _0, ...restTargetProps },
    } = useFlyout({
        // These defaults are also specified in the Tooltip component, but repeated here in case
        // someone wants to manually set up a Tooltip-like Flyout.
        ...tooltipFlyoutDefaults,
        ...rest,
        isOpen,
    });

    return {
        tooltipProps: flyoutProps,
        targetProps: {
            ...restTargetProps,
            ...(flyoutProps.isOpen && !visualOnly ? { "aria-describedby": flyoutProps.id } : {}),
            ...getDelayedOpenTargetProps({
                onMouseEnter,
                onMouseLeave,
                onFocus,
                onBlur,
            }),
        },
    };
};
